import React, { useState } from 'react';
import { Alert } from 'antd';
import { useIntl } from 'react-intl';
import dayjs from 'dayjs';
import styled from 'styled-components';

import FormDatePickerField from '@components/form/form-date-picker-field';
import FormSelectField from '@components/form/form-select-field';
import FormTextField from '@components/form/form-text-field';
import MrzScanModal from '@domain/mrz-scan-modal';
import { mrzScanRequest } from '@services/mrz';
import { useVismaConfiguration } from '@services/visitor-management';

import RightArrow from '../../assets/right-arrow.svg';
import MRZScan from '../../assets/scanner.svg';
import { useRegisterPage } from './register-page-context';

const Container = styled.div`
  width: 100%;
`;

const HeaderText = styled.h1`
  font-size: 30px;
  font-weight: 600;
  color: ${({ theme }) => theme.text};
  border-bottom: 1px solid ${({ theme }) => theme.border};
  padding-bottom: 16px;

  @media only screen and (max-width: 1200px) {
    margin: 16px 20px;
  }
`;

const HeaderTextWithoutBorder = styled.h1`
  font-size: 30px;
  font-weight: 600;
  color: ${({ theme }) => theme.text};
  padding-bottom: 16px;

  @media only screen and (max-width: 1200px) {
    font-size: 24px;
    margin: 16px 20px;
  }
`;

const DescText = styled.h6`
  font-size: 16px;
  font-weight: 400;
  color: ${({ theme }) => theme.textSecondary};
  margin: 24px 0;
  white-space: break-spaces;
  @media only screen and (max-width: 1200px) {
    padding: 0 20px;
  }
`;

const SubHeaderContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin: 2em 0;
`;

const InputContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 24px;

  @media only screen and (max-width: 1200px) {
    grid-template-columns: 1fr;
    margin: 16px 20px;
  }
`;
const MRZScanContainer = styled.div`
  background: ${({ theme }) => theme.primary};
  border-radius: 9px;
  padding: 16px;
  display: flex;
  align-items: center;
  width: 450px;
  cursor: pointer;
  transition: 0.5s;
  &:hover {
    transition: 0.5s;
    opacity: 0.8;
  }
  & > .scanner {
    width: 50px;
    height: 50px;
    background: ${({ theme }) => theme.primary};
    border-radius: 4px;
    display: flex;
    align-items: center;
    justify-content: center;
  }
  & > .context {
    width: 350px;
    color: ${({ theme }) => theme.white};
    margin: 0 1em;
    & > h4 {
      font-size: 20px;
      font-weight: 600;
    }
    & > p {
      font-weight: 400;
      line-height: 16px;
      margin-top: 8px;
      max-width: 300px;
    }
  }
  @media only screen and (max-width: 1200px) {
    display: none;
  }
`;

function RegisterForm() {
  const intl = useIntl();
  const { setFormField } = useRegisterPage()!;
  const { configuration } = useVismaConfiguration();
  const { data_field, title, description, mrz_module, personal_data_warning } =
    configuration?.personalData ?? {};
  const isRequired = (field: string) =>
    !!data_field?.[field as keyof typeof data_field]?.required;
  const isDisabled = (field: string) =>
    !!data_field?.[field as keyof typeof data_field]?.disabled;
  const [openMrzModal, setOpenMrzModal] = useState(false);

  interface ScanStatus {
    isFirstScanStarted?: boolean;
    isScanning?: boolean;
    isMRZerror?: boolean;
    isSuccess?: boolean;
    errorNumber?: number;
    errorCode?: string;
    errorText?: string;
  }

  const [scanStatus, setScanStatus] = useState<ScanStatus>({
    isFirstScanStarted: false,
    isScanning: false,
    isMRZerror: false,
    isSuccess: false,
    errorNumber: 0,
    errorCode: ''
  });

  interface MrzParsedData {
    documentNumber: string;
    dateBirth: string;
    dateExpiration: string;
    firstName: string;
    middleName?: string;
    lastName: string;
    lastName2?: string;
    type: string;
  }

  interface MrzResponseData {
    mrzText: string;
    parsed: MrzParsedData | null;
    versionService: string;
    errorMessage: string;
    errorCode: string;
    errorNumber: number;
    errorText?: string;
  }

  //R230331: Modified to be able to display error codes
  const handleScan = async () => {
    if (scanStatus?.isScanning) return;
    setScanStatus({
      isMRZerror: false,
      isFirstScanStarted: true,
      isScanning: true
    });
    try {
      let mrzRes = await mrzScanRequest();
      // --- AWAIT ---
      const data = mrzRes.data as MrzResponseData;
      // eslint-disable-next-line eqeqeq
      if (data?.errorNumber != 0) {
        // Reader is not available
        // Reader not in service
        // Reader not found
        // Timeout
        setScanStatus({
          errorNumber: data.errorNumber,
          errorCode: data.errorCode,
          errorText: data.errorText,
          isScanning: false,
          isMRZerror: true,
          isFirstScanStarted: true
        });
        return;
      } // error

      if (!data?.parsed) {
        // no error code AND data filed not exists or pared field not exists.. this should never happen
        setScanStatus({
          errorNumber: 219,
          errorCode: 'parseError',
          errorText: 'MRZ not parsed',
          isScanning: false,
          isMRZerror: true,
          isFirstScanStarted: true
        });
        return;
      } // not parsed
      else {
        // success, parsed
        // Document found
        const dateFormatter = (date: string | Date | number) => {
          let newDate = new Date(date).getTime();
          let formattedDate = new Date(newDate);
          return dayjs(formattedDate);
        };
        const readedData = data.parsed;
        setFormField('docNo', readedData.documentNumber);
        setFormField('dateOfBirth', dateFormatter(readedData.dateBirth));
        setFormField('docExpiryDate', dateFormatter(readedData.dateExpiration));
        setFormField(
          'fullname',
          [
            readedData.firstName,
            readedData?.middleName,
            readedData.lastName,
            readedData?.lastName2
          ]
            .filter(Boolean)
            .join(' ')
        );
        setFormField(
          'documentType',
          readedData.type === 'I'
            ? 'idcard'
            : readedData.type === 'P'
              ? 'passport'
              : ''
        );
        setScanStatus({
          isScanning: false,
          isMRZerror: false,
          isFirstScanStarted: true
        });
        setTimeout(() => {
          setOpenMrzModal(false);
          let el = document.getElementById('leftContainer');
          el?.scrollTo({ top: 3000, behavior: 'smooth' });
          setScanStatus({
            isScanning: false,
            isMRZerror: false,
            isFirstScanStarted: true
          });
        }, 500); //R230331: 2000 was too slow. BTW, why need we wait here?
      } // parsed
    } catch (err) {
      // try
      setScanStatus({
        isScanning: false,
        isMRZerror: true,
        isFirstScanStarted: true
      });
      console.log(err);
    } // catch
  };

  return (
    <Container>
      <MrzScanModal
        open={openMrzModal}
        scanStatus={scanStatus}
        closeModal={() => {
          setScanStatus({
            isFirstScanStarted: false,
            isScanning: false,
            isMRZerror: false,
            isSuccess: false,
            errorNumber: 0,
            errorCode: ''
          });
          setOpenMrzModal(false);
        }}
        handleScan={handleScan}
      />
      <HeaderText>{title}</HeaderText>
      <DescText>{description}</DescText>

      <SubHeaderContainer>
        <HeaderTextWithoutBorder>
          {intl.formatMessage({ id: 'MANUAL_DATA_INPUTS' })}
        </HeaderTextWithoutBorder>
        {mrz_module?.isActive && (
          <MRZScanContainer
            onClick={() => {
              setOpenMrzModal(true);
            }}
          >
            <div className="scanner">
              <img src={MRZScan} alt="scanner" />
            </div>
            <div className="context">
              <h4>{intl.formatMessage({ id: 'MRZ_SCAN' })}</h4>
              <p>{intl.formatMessage({ id: 'MRZ_SCAN_BUTTON_DESC' })}</p>
            </div>
            <img src={RightArrow} alt="right-arrow" />
          </MRZScanContainer>
        )}
      </SubHeaderContainer>

      <InputContainer>
        <FormTextField
          name="fullname"
          required={isRequired('fullname')}
          label={intl.formatMessage({ id: 'FULL_NAME' })}
          autoComplete="off"
        />
        {!isDisabled('mother_name') && (
          <FormTextField
            name="mothersName"
            required={isRequired('mother_name')}
            label={intl.formatMessage({ id: 'MOTHERS_NAME' })}
            autoComplete="off"
          />
        )}
        <FormDatePickerField
          name="dateOfBirth"
          required={isRequired('birth_date')}
          label={intl.formatMessage({ id: 'DATE_OF_BIRTH' })}
        />
        <FormSelectField
          name="documentType"
          required={isRequired('document_type')}
          label={intl.formatMessage({ id: 'TYPE_OF_DOCUMENT' })}
          options={[
            { value: 'id_card', label: intl.formatMessage({ id: 'ID_CARD' }) },
            {
              value: 'passport',
              label: intl.formatMessage({ id: 'PASSPORT' })
            }
          ]}
        />
        <FormTextField
          name="docNo"
          required={isRequired('document_number')}
          label={intl.formatMessage({ id: 'DOC_NO' })}
          autoComplete="off"
        />
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          <FormDatePickerField
            name="docExpiryDate"
            required={isRequired('document_expiry_date')}
            label={intl.formatMessage({ id: 'DOC_EXPIRY_DATE' })}
          />
          {personal_data_warning && (
            <Alert
              type="warning"
              description={personal_data_warning}
              showIcon
            />
          )}
        </div>
        {!isDisabled('company_name') && (
          <FormTextField
            name="companyName"
            required={isRequired('company_name')}
            label={intl.formatMessage({ id: 'COMPANY_NAME' })}
            autoComplete="off"
          />
        )}
        {!isDisabled('phonenumber') && (
          <FormTextField
            name="phoneNumber"
            required={isRequired('phonenumber')}
            label={intl.formatMessage({ id: 'PHONE_NUMBER' })}
            autoComplete="off"
          />
        )}
      </InputContainer>
    </Container>
  );
}

export default RegisterForm;
